import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAIAgentCallChatContext, useAIAgentCenterContext } from 'contexts/ai-agent-center';
import { AIContactEvaluationOption, CallSessionStatus } from 'utils';
import { BellIcon, BellOffIcon, NeutralIcon } from 'assets';
import { Spin, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { aiAgentCenterMessages } from 'messages';
import { twMerge } from 'tailwind-merge';
import { useContactDetailByPhone } from 'hooks/contacts';
import { useFindOneContactSentiment, useUpdateCallSession } from 'hooks';
import { message } from 'components/common';

const AIAgentCallChatHeader: React.FC = () => {
  const { setCallSession } = useAIAgentCenterContext();
  const { callSession } = useAIAgentCallChatContext();

  const { contactSentiment, isLoadingContactSentiment } = useFindOneContactSentiment({
    phoneNumber: callSession?.customerPhoneNumber
  });
  const { updateCallSession, isLoading } = useUpdateCallSession();
  const notify = callSession.isAlerted;

  const [timer, setTimer] = useState('00:00');

  const startTime = useMemo(() => dayjs(callSession.receivedAt).toDate(), [callSession.receivedAt]);

  const aiEvaluationIcon = useMemo(() => {
    const aiPreview = contactSentiment?.sentiment;
    if (!aiPreview) return <NeutralIcon height={36} width={36} />;

    const iconFunction =
      AIContactEvaluationOption[contactSentiment?.sentiment as keyof typeof AIContactEvaluationOption]?.icon;
    return iconFunction ? iconFunction(36) : <NeutralIcon height={36} width={36} />;
  }, [contactSentiment?.sentiment]);

  const formatDuration = useCallback((ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const diff = now.getTime() - startTime.getTime();
      setTimer(formatDuration(diff));
    };

    updateTimer();

    const intervalId = setInterval(updateTimer, 1000);
    if (callSession.callStatus !== CallSessionStatus.PROCESSING) {
      clearInterval(intervalId);
    }
    return () => clearInterval(intervalId);
  }, [startTime, formatDuration, callSession.callStatus]);

  const handleToggleNotify = useCallback(() => {
    updateCallSession(
      {
        callSessionId: callSession.callSessionId,
        isAlerted: !notify
      },
      (data) => {
        setCallSession({ ...callSession, isAlerted: data.isAlerted });
        message.success(aiAgentCenterMessages.alertCallSuccess);
      }
    );
  }, [callSession, notify, updateCallSession, setCallSession]);

  return (
    <div className='relative flex h-16 items-center border-b px-4'>
      <div className='relative flex w-full items-center gap-2'>
        <div className='absolute left-1/2 flex h-full -translate-x-1/2 flex-col items-center justify-center'>
          <Typography.Text className='mb-0.5 font-semibold'>{callSession.customerPhoneNumber}</Typography.Text>
          {callSession.callStatus === CallSessionStatus.PROCESSING && (
            <Typography.Text className='text-sm text-colorTextPlaceholder'>{timer}</Typography.Text>
          )}
        </div>

        <div className='h-9 w-9'>
          <Spin spinning={isLoadingContactSentiment || isLoading}>{aiEvaluationIcon}</Spin>
        </div>

        <Tooltip
          prefixCls='custom-tooltip'
          title={
            <Typography.Text className='text-xs font-semibold text-white'>
              {notify ? aiAgentCenterMessages.turnOffWarning : aiAgentCenterMessages.turnOnWarning}
            </Typography.Text>
          }
        >
          <div
            onClick={handleToggleNotify}
            className={twMerge(
              'ml-auto flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border',
              notify ? 'border-mainColor1 bg-[#1564E814]' : 'border-colorBorder bg-white'
            )}
          >
            {notify ? (
              <BellOffIcon className='text-mainColor1' height={16} width={16} />
            ) : (
              <BellIcon className='text-colorTextBase' height={16} width={16} />
            )}
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

export default AIAgentCallChatHeader;
